// websocket.ts - WebSocket client for real-time widget communication

import type { NovaError } from './types';
import type { UsageStatus } from './auth';

export interface WebSocketMessage {
  type: string;
  [key: string]: unknown;
}

export interface ConnectedMessage extends WebSocketMessage {
  type: 'connected';
  sessionId: string;
  siteId: string;
  siteName?: string;
}

export interface ChatMessage extends WebSocketMessage {
  type: 'message';
  content: string;
  messageId: string;
  timestamp: string;
  /** false when the reply did not spend the usage allowance */
  billable?: boolean;
  /** The allowance after this reply, as percentages (signed-in users) */
  usage?: UsageStatus | null;
  /** Follow-up questions to offer as chips under the answer */
  suggestions?: string[];
  /** true when this reply confirms a wallet transaction that succeeded */
  celebrate?: boolean;
}

export interface TypingMessage extends WebSocketMessage {
  type: 'typing';
  isTyping: boolean;
}

export interface ErrorMessage extends WebSocketMessage {
  type: 'error';
  code: string;
  message: string;
  retryAfterMs?: number;
}

export interface NovaWebSocketConfig {
  apiKey: string;
  sessionId: string;
  apiUrl?: string;
  onOpen?: () => void;
  onClose?: (code: number, reason: string) => void;
  onMessage?: (message: ChatMessage) => void;
  onTyping?: (isTyping: boolean) => void;
  onError?: (error: NovaError) => void;
  onConnected?: (data: ConnectedMessage) => void;
  reconnect?: boolean;
  reconnectInterval?: number;
  maxReconnectAttempts?: number;
}

export class NovaWebSocket {
  private ws: WebSocket | null = null;
  private config: Required<NovaWebSocketConfig>;
  private reconnectAttempts = 0;
  private reconnectTimeout: ReturnType<typeof setTimeout> | null = null;
  private pingInterval: ReturnType<typeof setInterval> | null = null;
  private isIntentionallyClosed = false;

  constructor(config: NovaWebSocketConfig) {
    this.config = {
      apiUrl: 'wss://nova.zeroauthoritydao.com',
      onOpen: () => {},
      onClose: () => {},
      onMessage: () => {},
      onTyping: () => {},
      onError: () => {},
      onConnected: () => {},
      reconnect: true,
      reconnectInterval: 3000,
      maxReconnectAttempts: 5,
      ...config,
    };
  }

  /**
   * Connect to WebSocket server
   */
  connect(): void {
    if (this.ws?.readyState === WebSocket.OPEN) {
      return; // Already connected
    }

    this.isIntentionallyClosed = false;

    // Build WebSocket URL
    const baseUrl = this.config.apiUrl
      .replace(/^http/, 'ws')
      .replace(/\/$/, '');
    const url = `${baseUrl}/api/widget/ws?apiKey=${encodeURIComponent(this.config.apiKey)}&sessionId=${encodeURIComponent(this.config.sessionId)}`;

    try {
      this.ws = new WebSocket(url);
      this.setupEventHandlers();
    } catch (error) {
      this.handleError({
        code: 'connection_failed',
        message: 'Failed to create WebSocket connection',
      });
    }
  }

  /**
   * Disconnect from WebSocket server
   */
  disconnect(): void {
    this.isIntentionallyClosed = true;
    this.stopPing();
    this.clearReconnectTimeout();

    if (this.ws) {
      this.ws.close(1000, 'Client disconnect');
      this.ws = null;
    }
  }

  /**
   * Send a chat message
   */
  sendMessage(content: string, metadata?: Record<string, unknown>): boolean {
    return this.send({
      type: 'message',
      content,
      metadata,
    });
  }

  /**
   * Send typing indicator
   */
  sendTyping(isTyping: boolean): boolean {
    return this.send({
      type: 'typing',
      isTyping,
    });
  }

  /**
   * Check if connected
   */
  isConnected(): boolean {
    return this.ws?.readyState === WebSocket.OPEN;
  }

  /**
   * Get connection state
   */
  getState(): 'connecting' | 'open' | 'closing' | 'closed' {
    if (!this.ws) return 'closed';

    switch (this.ws.readyState) {
      case WebSocket.CONNECTING:
        return 'connecting';
      case WebSocket.OPEN:
        return 'open';
      case WebSocket.CLOSING:
        return 'closing';
      case WebSocket.CLOSED:
      default:
        return 'closed';
    }
  }

  private setupEventHandlers(): void {
    if (!this.ws) return;

    this.ws.onopen = () => {
      this.reconnectAttempts = 0;
      this.startPing();
      this.config.onOpen();
    };

    this.ws.onclose = (event) => {
      this.stopPing();
      this.config.onClose(event.code, event.reason);

      // Attempt reconnection if not intentionally closed
      if (!this.isIntentionallyClosed && this.config.reconnect) {
        this.scheduleReconnect();
      }
    };

    this.ws.onerror = () => {
      // Error details are limited in browser WebSocket API
      // The close event will fire after this with more info
    };

    this.ws.onmessage = (event) => {
      try {
        const message = JSON.parse(event.data) as WebSocketMessage;
        this.handleMessage(message);
      } catch {
        // Ignore parse errors
      }
    };
  }

  private handleMessage(message: WebSocketMessage): void {
    switch (message.type) {
      case 'connected':
        this.config.onConnected(message as ConnectedMessage);
        break;

      case 'message':
        this.config.onMessage(message as ChatMessage);
        break;

      case 'typing':
        this.config.onTyping((message as TypingMessage).isTyping);
        break;

      case 'error':
        const errorMsg = message as ErrorMessage;
        this.handleError({
          code: errorMsg.code,
          message: errorMsg.message,
          retryAfterMs: errorMsg.retryAfterMs,
        });
        break;

      case 'pong':
        // Pong received, connection is alive
        break;
    }
  }

  private handleError(error: NovaError): void {
    this.config.onError(error);
  }

  private send(data: WebSocketMessage): boolean {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      return false;
    }

    try {
      this.ws.send(JSON.stringify(data));
      return true;
    } catch {
      return false;
    }
  }

  private startPing(): void {
    this.stopPing();
    this.pingInterval = setInterval(() => {
      this.send({ type: 'ping' });
    }, 25000); // Ping every 25 seconds
  }

  private stopPing(): void {
    if (this.pingInterval) {
      clearInterval(this.pingInterval);
      this.pingInterval = null;
    }
  }

  private scheduleReconnect(): void {
    if (this.reconnectAttempts >= this.config.maxReconnectAttempts) {
      this.handleError({
        code: 'max_reconnect_attempts',
        message: 'Maximum reconnection attempts reached',
      });
      return;
    }

    this.clearReconnectTimeout();

    const delay = this.config.reconnectInterval * Math.pow(1.5, this.reconnectAttempts);
    this.reconnectAttempts++;

    this.reconnectTimeout = setTimeout(() => {
      this.connect();
    }, delay);
  }

  private clearReconnectTimeout(): void {
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }
  }
}

/**
 * Check if WebSocket is supported in the current environment
 */
export function isWebSocketSupported(): boolean {
  return typeof WebSocket !== 'undefined';
}

export default NovaWebSocket;
